import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ChevronRight, 
  ChevronDown,
  Scissors,
  Zap,
  Target,
  Stethoscope,
  Syringe,
  Video,
  List,
  Circle,
  Baby
} from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import VideoCard from '../components/VideoCard';
import UnifiedSearch from '../components/UnifiedSearch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Comprehensive Procedure categories
const procedureCategories = [
  {
    id: 'stone',
    name: 'Kidney Stone Procedures',
    icon: Circle,
    description: 'Ureteroscopy, ESWL, PCNL, and other stone treatments',
    articleKeywords: ['Ureteroscopy', 'Lithotripsy', 'ESWL', 'PCNL', 'Nephrolithotomy', 'Stone Removal', 'Laser Lithotripsy'],
    staticItems: []
  },
  {
    id: 'bph',
    name: 'BPH / Prostate Procedures',
    icon: Zap,
    description: 'TURP, GreenLight laser, Aquablation, Rezum, UroLift',
    articleKeywords: ['vs TURP', 'vs Aquablation', 'GreenLight', 'Prostate Outlet', 'BPH Treatment Options', 'Rezum', 'UroLift', 'MIST', 'Office-Based'],
    staticItems: []
  },
  {
    id: 'diagnostic',
    name: 'Diagnostic Procedures',
    icon: Stethoscope,
    description: 'Cystoscopy, biopsies, urodynamics',
    articleKeywords: ['Biopsy', 'Cystoscopy', 'Bladder Biopsy', 'Renal Mass Biopsy', 'Urodynamic'],
    staticItems: []
  },
  {
    id: 'cancer-surgery',
    name: 'Cancer Surgery',
    icon: Scissors,
    description: 'Prostatectomy, cystectomy, nephrectomy, orchiectomy',
    articleKeywords: ['Prostatectomy', 'Cystectomy', 'Orchiectomy', 'Nephrectomy', 'Nephroureterectomy', 'Partial Nephrectomy', 'Radical'],
    excludeKeywords: ['vs Radiation'],
    staticItems: []
  },
  {
    id: 'cancer-treatment',
    name: 'Cancer Treatment (Non-Surgical)',
    icon: Target,
    description: 'BCG therapy, radiation, ablation, TURBT',
    articleKeywords: ['BCG Therapy', 'TURBT', 'Proton Beam', 'Photon Beam', 'Radiation Therapy', 'Ablative', 'Cryoablation', 'Radiofrequency', 'Prostatectomy vs Radiation', 'Active Surveillance'],
    staticItems: []
  },
  {
    id: 'incontinence',
    name: 'Incontinence Procedures',
    icon: Syringe,
    description: 'Slings, artificial sphincter, Botox, nerve stimulation',
    articleKeywords: ['Sling', 'Sphincter', 'Botox', 'InterStim', 'Incontinence Treatment', 'Incontinence: A Complete Guide'],
    staticItems: []
  },
  {
    id: 'reconstruction',
    name: 'Reconstructive Procedures',
    icon: Scissors,
    description: 'Urethroplasty, pyeloplasty, ureteral reimplantation',
    articleKeywords: ['Urethroplasty', 'Pyeloplasty', 'Reimplant', 'Stricture', 'UPJ'],
    staticItems: []
  },
  {
    id: 'mens-health',
    name: "Men's Health Procedures",
    icon: Syringe,
    description: 'Vasectomy, circumcision, hydrocelectomy, spermatocelectomy, penile procedures',
    articleKeywords: ['Circumcision', 'Vasectomy', 'Penile', 'Implant', 'Peyronie', 'Testosterone Replacement', 'Varicocele', 'Hydrocelectomy', 'Spermatocelectomy'],
    excludeKeywords: ['Cancer', 'Pediatric'],
    staticItems: []
  },
  {
    id: 'pediatric',
    name: 'Pediatric Urology',
    icon: Baby,
    description: 'Orchiopexy, hydrocele repair, hypospadias, circumcision in children',
    articleKeywords: ['Pediatric', 'Orchiopexy', 'Hypospadias', 'Chordee', 'VUR', 'Vesicoureteral'],
    staticItems: []
  },
  {
    id: 'catheter',
    name: 'Catheter & Stent Care',
    icon: Stethoscope,
    description: 'Foley catheter, suprapubic catheter, ureteral stent management',
    articleKeywords: ['Catheter', 'Suprapubic', 'Stent'],
    staticItems: []
  }
];

const ProceduresPage = () => {
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showAllExpanded, setShowAllExpanded] = useState(false);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const [articlesRes, videosRes] = await Promise.all([
          axios.get(`${API}/blog?limit=100`),
          axios.get(`${API}/videos?limit=50`)
        ]);
        
        // Filter to only procedure-related articles
        const procedureKeywords = procedureCategories.flatMap(c => c.articleKeywords);
        const filtered = articlesRes.data.filter(article => 
          procedureKeywords.some(keyword => article.title.includes(keyword))
        );
        setArticles(filtered);
        
        // Filter videos for procedures
        const procedureVideos = videosRes.data.filter(v => {
          const category = (v.category || '').toLowerCase();
          const tags = v.tags || [];
          return category === 'procedure' || 
                 tags.some(t => ['procedure', 'surgery', 'biopsy', 'cystoscopy', 'ureteroscopy', 'lithotripsy', 'stent', 'catheter', 'vasectomy'].includes(t.toLowerCase()));
        });
        setVideos(procedureVideos.slice(0, 6));
      } catch (error) {
        console.error("Error loading content:", error);
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, []);

  const categorizeProcedure = (article) => {
    const title = (article.title || '').toLowerCase();
    for (const cat of procedureCategories) {
      if (cat.articleKeywords.some(keyword => title.includes(keyword.toLowerCase()))) {
        // Check exclude keywords if present
        if (cat.excludeKeywords && cat.excludeKeywords.some(ex => title.includes(ex.toLowerCase()))) {
          continue;
        }
        return cat.id;
      }
    }
    return 'other';
  };

  const getArticlesByCategory = (categoryId) => {
    return articles.filter(article => categorizeProcedure(article) === categoryId);
  };

  const getCategoryItemCount = (cat) => {
    return getArticlesByCategory(cat.id).length + (cat.staticItems?.length || 0);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allCategoryIds = procedureCategories
      .filter(cat => getCategoryItemCount(cat) > 0)
      .map(cat => cat.id);
    setExpandedCategories(new Set(allCategoryIds));
    setShowAllExpanded(true);
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
    setShowAllExpanded(false);
  };

  // Get all items (articles + static) for Show All
  const getAllItems = () => {
    const items = [];
    // Add all articles
    articles.forEach(article => {
      items.push({
        type: 'article',
        title: article.title,
        link: `/procedures/${article.id}`,
        id: article.id
      });
    });
    // Add all static items
    procedureCategories.forEach(cat => {
      (cat.staticItems || []).forEach(item => {
        items.push({
          type: 'static',
          title: item.title,
          link: item.link,
          description: item.description,
          isAfterVisit: item.isAfterVisit,
          id: item.link
        });
      });
    });
    return items.sort((a, b) => a.title.localeCompare(b.title));
  };

  const allItems = getAllItems();

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Home', path: '/' },
        { label: 'Procedures', path: '/procedures' }
      ]} />

      <div className="hub-header" data-testid="procedures-header">
        <Scissors size={48} className="hub-icon" />
        <h1>Urologic Procedures</h1>
        <p>Learn about surgical treatments, what to expect, and how to prepare.</p>
      </div>

      {/* Search Bar */}
      <div className="hub-search-wrapper">
        <UnifiedSearch placeholder="Search procedures, surgeries, treatments..." variant="hub" />
      </div>

      {/* Expand/Collapse All Buttons */}
      <div className="accordion-controls" data-testid="accordion-controls">
        <button onClick={expandAll} className="accordion-control-btn">
          Expand All
        </button>
        <button onClick={collapseAll} className="accordion-control-btn">
          Collapse All
        </button>
      </div>

      {/* Accordion List */}
      {loading ? (
        <div className="loading-state">Loading procedures...</div>
      ) : (
        <div className="conditions-accordion" data-testid="procedures-accordion">
          {/* Show All Accordion */}
          <div className="accordion-section accordion-show-all" data-testid="accordion-show-all">
            <button
              className={`accordion-header ${showAllExpanded ? 'expanded' : ''}`}
              onClick={() => setShowAllExpanded(!showAllExpanded)}
              data-testid="accordion-header-show-all"
            >
              <div className="accordion-header-left">
                <List size={22} className="accordion-icon" />
                <div className="accordion-title-group">
                  <h3>Show All Procedures</h3>
                  <span className="accordion-description">Browse all procedures alphabetically</span>
                </div>
              </div>
              <div className="accordion-header-right">
                <span className="accordion-count">{allItems.length}</span>
                {showAllExpanded ? (
                  <ChevronDown size={20} className="accordion-chevron" />
                ) : (
                  <ChevronRight size={20} className="accordion-chevron" />
                )}
              </div>
            </button>
            
            {showAllExpanded && (
              <div className="accordion-content accordion-content-all" data-testid="accordion-content-show-all">
                {allItems.map(item => (
                  <Link 
                    to={item.link}
                    key={item.id}
                    className="accordion-article-link"
                  >
                    <span className="article-title">{item.title}</span>
                    <ChevronRight size={16} className="article-arrow" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Category Accordions */}
          {procedureCategories.map(cat => {
            const IconComponent = cat.icon;
            const categoryArticles = getArticlesByCategory(cat.id);
            const count = getCategoryItemCount(cat);
            const isExpanded = expandedCategories.has(cat.id);
            
            if (count === 0) return null;
            
            return (
              <div key={cat.id} className="accordion-section" data-testid={`accordion-${cat.id}`}>
                <button
                  className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleCategory(cat.id)}
                  data-testid={`accordion-header-${cat.id}`}
                >
                  <div className="accordion-header-left">
                    <IconComponent size={22} className="accordion-icon" />
                    <div className="accordion-title-group">
                      <h3>{cat.name}</h3>
                      <span className="accordion-description">{cat.description}</span>
                    </div>
                  </div>
                  <div className="accordion-header-right">
                    <span className="accordion-count">{count}</span>
                    {isExpanded ? (
                      <ChevronDown size={20} className="accordion-chevron" />
                    ) : (
                      <ChevronRight size={20} className="accordion-chevron" />
                    )}
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="accordion-content" data-testid={`accordion-content-${cat.id}`}>
                    {/* Static items first */}
                    {(cat.staticItems || []).map(item => (
                      <Link 
                        to={item.link}
                        key={item.link}
                        className={`accordion-article-link ${item.isAfterVisit ? 'aftercare-link' : ''}`}
                      >
                        <div className="article-title-group">
                          <span className="article-title">{item.title}</span>
                          {item.description && <span className="article-desc">{item.description}</span>}
                          {item.isAfterVisit && <span className="aftercare-badge">After Visit</span>}
                        </div>
                        <ChevronRight size={16} className="article-arrow" />
                      </Link>
                    ))}
                    {/* Dynamic articles */}
                    {categoryArticles.map(article => (
                      <Link 
                        to={`/procedures/${article.id}`}
                        key={article.id}
                        className="accordion-article-link"
                      >
                        <span className="article-title">{article.title}</span>
                        <ChevronRight size={16} className="article-arrow" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Link to After Visit */}
      <div className="procedures-cta">
        <h3>Had a procedure recently?</h3>
        <p>Find your post-procedure care instructions</p>
        <Link to="/after-visit" className="btn-primary">
          View After-Visit Care Guides
        </Link>
      </div>

      {/* Procedure Videos Section */}
      {videos.length > 0 && (
        <section className="hub-videos-section" data-testid="procedures-videos">
          <div className="hub-videos-header">
            <h2><Video size={24} /> Procedure Videos</h2>
            <Link to="/videos?category=procedure" className="view-all-link">
              View All Procedure Videos <ChevronRight size={16} />
            </Link>
          </div>
          <div className="hub-videos-grid">
            {videos.map(video => (
              <VideoCard key={video.id} video={video} compact={true} />
            ))}
          </div>
        </section>
      )}

      <div className="hub-disclaimer">
        <p>
          <strong>Medical Disclaimer:</strong> This information is for educational purposes only 
          and is not a substitute for professional medical advice. Always consult your physician.
        </p>
      </div>
    </PageWrapper>
  );
};

export default ProceduresPage;
